import { useEffect, useState } from 'react';
import api from '../api/client';

/**
 * Левый блок: текущий список задач сессии.
 * Показывается и ученику, и преподавателю.
 */

function getLastActiveStorageKey(sessionId) {
  return `collab:lastActiveTask:${sessionId}`;
}

function sessionTaskNumber(item) {
  const n = item?.source_number;
  if (n != null && String(n).trim() !== '') return String(n).trim();
  if (item?.task != null) return String(item.task);
  return '';
}

function sessionTaskHeaderTitle(item) {
  const title = typeof item?.task_title === 'string' ? item.task_title.trim() : '';
  if (title) return title;
  const num = sessionTaskNumber(item);
  return num || 'Текст задачи';
}

function sessionTaskTagNames(item) {
  const raw = Array.isArray(item?.task_tags) ? item.task_tags : [];
  return raw
    .map((t) => (typeof t === 'string' ? t : t?.name))
    .filter((name) => typeof name === 'string' && name.trim())
    .map((name) => name.trim());
}

function saveLastActive(sessionId, itemId, taskId) {
  if (!sessionId || !itemId) return;
  try {
    localStorage.setItem(getLastActiveStorageKey(sessionId), JSON.stringify({ itemId, taskId }));
  } catch {
    // ignore
  }
}

function notifyActiveTaskChange(active, onActiveTaskChange) {
  if (!active || typeof onActiveTaskChange !== 'function') return;
  onActiveTaskChange({
    taskId: active.task,
    title: sessionTaskHeaderTitle(active),
    task_tags: active.task_tags || [],
    source_name: active.source_name,
    source_number: active.source_number,
    source_author: active.source_author,
    year: active.year,
    answer_type: active.answer_type || '',
    expected_answer: active.expected_answer || '',
    answer_spec: active.answer_spec && typeof active.answer_spec === 'object' ? active.answer_spec : {},
    solution_code: active.solution_code || '',
    solution_explanation: active.solution_explanation || '',
  });
}

function syncActiveTaskToYdocMeta(ydoc, active) {
  if (!ydoc || !active) return;
  const meta = ydoc.getMap('sessionMeta');
  // Одна транзакция: иначе observe в CollaborativeTaskPanel может увидеть новый id
  // со старым statement и «залипнуть» на тексте предыдущей задачи.
  ydoc.transact(() => {
    meta.set('activeTaskId', active.task);
    meta.set('activeTaskTitle', sessionTaskHeaderTitle(active));
    meta.set('activeTaskStatement', active.task_statement || '');
    try {
      meta.set('activeTaskAttachmentsJson', JSON.stringify(active.statement_attachments || []));
    } catch {
      meta.set('activeTaskAttachmentsJson', '[]');
    }
    try {
      meta.set('activeTaskTagsJson', JSON.stringify(active.task_tags || []));
    } catch {
      meta.set('activeTaskTagsJson', '[]');
    }
    meta.set('activeSourceName', active.source_name || '');
    meta.set('activeSourceNumber', active.source_number || '');
    meta.set('activeSourceAuthor', active.source_author || '');
    meta.set('activeYear', active.year ?? '');
    meta.set('activeAnswerType', active.answer_type || '');
    meta.set('activeExpectedAnswer', active.expected_answer || '');
    try {
      meta.set(
        'activeAnswerSpecJson',
        JSON.stringify(active.answer_spec && typeof active.answer_spec === 'object' ? active.answer_spec : {}),
      );
    } catch {
      meta.set('activeAnswerSpecJson', '{}');
    }
    meta.set('activeSolutionCode', active.solution_code || '');
    meta.set('activeSolutionExplanation', active.solution_explanation || '');
  });
}

function loadLastActive(sessionId) {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(getLastActiveStorageKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.itemId !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearLastActive(sessionId) {
  if (!sessionId) return;
  try {
    localStorage.removeItem(getLastActiveStorageKey(sessionId));
  } catch {
    // ignore
  }
}

export default function CollaborativeSessionTaskList({
  sessionId,
  isTeacher,
  ydoc,
  refreshNonce,
  onActiveTaskChange,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [switchRequiresAuth, setSwitchRequiresAuth] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    fetchItems();
  }, [sessionId, refreshNonce]);

  // ydoc подключается позже списка — прокидываем метаданные активной задачи в CRDT
  useEffect(() => {
    if (!ydoc) return;
    const active = items.find((i) => i.is_active);
    if (active) syncActiveTaskToYdocMeta(ydoc, active);
  }, [ydoc, items]);

  async function fetchItems() {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/collaborative/sessions/${sessionId}/tasks/`);
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
      const active = data.find((i) => i.is_active);
      if (active) {
        saveLastActive(sessionId, active.id, active.task);
        syncActiveTaskToYdocMeta(ydoc, active);
        notifyActiveTaskChange(active, onActiveTaskChange);
      } else if (!active) {
        const saved = loadLastActive(sessionId);
        const restored = saved?.itemId ? data.find((i) => i.id === saved.itemId) : null;
        if (restored) {
          setActive(restored.id, restored.task);
        } else if (saved) {
          clearLastActive(sessionId);
        }
      }
    } catch {
      setError('Не удалось загрузить список задач сессии.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function setActive(itemId, taskId) {
    try {
      const res = await api.patch(`/collaborative/sessions/${sessionId}/tasks/`, {
        active_id: itemId,
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const active = data.find((i) => i.is_active) || null;
      setSwitchRequiresAuth(false);
      setItems(data);
      saveLastActive(sessionId, itemId, taskId);
      if (active) {
        syncActiveTaskToYdocMeta(ydoc, active);
        notifyActiveTaskChange(active, onActiveTaskChange);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && detail === 'Authentication credentials were not provided.') {
        setSwitchRequiresAuth(true);
        setError('Чтобы переключать задачи, нужно войти в аккаунт.');
        return;
      }
    }
  }

  async function removeItem(id) {
    try {
      await api.delete(`/collaborative/sessions/${sessionId}/tasks/${id}/`);
      await fetchItems();
    } catch {
      // ignore
    }
  }

  async function duplicateItem(item) {
    try {
      await api.post(`/collaborative/sessions/${sessionId}/tasks/`, {
        task_id: item.task,
      });
      await fetchItems();
    } catch {
      // ignore
    }
  }

  function moveItem(id, direction) {
    const index = items.findIndex((i) => i.id === id);
    if (index === -1) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    const payload = {
      items: next.map((item, idx) => ({ id: item.id, order: idx + 1 })),
    };
    setItems(next);
    api
      .patch(`/collaborative/sessions/${sessionId}/tasks/`, payload)
      .catch(() => {});
  }

  const activeItem = items.find((i) => i.is_active);

  return (
    <div className="collab-session-task-list">
      {error && <div className="alert alert-error">{error}</div>}
      {switchRequiresAuth && !isTeacher && (
        <div className="alert alert-warning">
          Вы открыли сессию без авторизации. Переключение задач недоступно для гостей. Войдите в аккаунт и обновите страницу.
        </div>
      )}
      {loading && <p className="text-secondary">Загрузка списка...</p>}
      {!loading && items.length === 0 && (
        <p className="text-secondary">Задачи ещё не добавлены.</p>
      )}
      <ul className="collab-session-task-list-items">
        {items.map((item, index) => {
          const isActive = item.is_active;
          const number = sessionTaskNumber(item);
          const tags = sessionTaskTagNames(item);
          const title = typeof item.task_title === 'string' ? item.task_title.trim() : '';
          const visibleTags = tags.slice(0, 3);
          const hiddenTagCount = Math.max(0, tags.length - visibleTags.length);
          return (
            <li
              key={item.id}
              className={`collab-session-task-item ${isActive ? 'is-active' : ''}`}
            >
              <div className="collab-session-task-main">
                <div className="collab-session-task-title-row">
                  <span className="collab-session-task-index">{index + 1}.</span>
                  {number ? (
                    <span className="collab-session-task-number" title={`№ ${number}`}>
                      {number}
                    </span>
                  ) : null}
                  {visibleTags.length > 0 ? (
                    <span
                      className="collab-session-task-inline-tags"
                      aria-label="Теги задачи"
                      title={tags.join(', ')}
                    >
                      {visibleTags.map((name) => (
                        <span key={name} className="tag tag-compact" title={name}>
                          {name}
                        </span>
                      ))}
                      {hiddenTagCount > 0 ? (
                        <span className="tag tag-compact tag-compact-more" title={`Ещё тегов: ${hiddenTagCount}`}>
                          +{hiddenTagCount}
                        </span>
                      ) : null}
                    </span>
                  ) : title ? (
                    <span className="collab-session-task-title" title={title}>
                      {title}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="collab-session-task-actions">
                <button
                  type="button"
                  className="btn btn-xs btn-outline"
                  onClick={() => setActive(item.id, item.task)}
                  disabled={(activeItem && activeItem.id === item.id) || (switchRequiresAuth && !isTeacher)}
                >
                  Открыть
                </button>
                {isTeacher && (
                  <>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost"
                      onClick={() => moveItem(item.id, 'up')}
                      title="Выше"
                      aria-label="Выше"
                    >
                      <i className="fa-solid fa-arrow-up ui-icon" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost"
                      onClick={() => moveItem(item.id, 'down')}
                      title="Ниже"
                      aria-label="Ниже"
                    >
                      <i className="fa-solid fa-arrow-down ui-icon" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost"
                      onClick={() => duplicateItem(item)}
                      title="Дублировать"
                      aria-label="Дублировать"
                    >
                      <i className="fa-solid fa-copy ui-icon" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline btn-danger"
                      onClick={() => removeItem(item.id)}
                      title="Удалить"
                      aria-label="Удалить"
                    >
                      <i className="fa-solid fa-xmark ui-icon" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

